import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	AppBar,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Collapse,
	CssBaseline,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Drawer,
	IconButton,
	Link,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Toolbar,
	Typography
} from "@mui/material";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom";
import StorageIcon from "@mui/icons-material/Storage";
import LogoutIcon from "@mui/icons-material/Logout";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import BlockIcon from "@mui/icons-material/Block";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import DevicesIcon from "@mui/icons-material/Devices";
import MarginIcon from "@mui/icons-material/Margin";
import ConstructionIcon from "@mui/icons-material/Construction";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import VideocamIcon from "@mui/icons-material/Videocam";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import ReplayIcon from "@mui/icons-material/Replay";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PodcastsIcon from "@mui/icons-material/Podcasts";

import "./pageLayout.css";
import { useAppDispatch, useAppSelector } from "../../redux";
import { ReactComponent as ServiceOfflineImage } from "../../assets/images/service-offline.svg";
import { ReactComponent as UpArrowIconImage } from "../../assets/icons/arrow-up.svg";
import { ReactComponent as DownArrowIconImage } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as GateLoaderIcon } from "../../assets/icons/gate-loader.svg";
import DateRangeSelectionModal, { SelectedRangeType } from "../../common/DateRangeSelectionModal";
import moment from "moment";
import {
	CONFIG_MODE_E,
	DEPLOYMENT_ERROR_MESSAGE,
	EVENT_LOGS_REQUEST_TIME_FORMAT,
	FORCE_SYNC_STATUS_POLLING_INTERVAL,
	NOTIFICATION_AUTO_DISMISS_TIME,
	RPI_DEVICE_NAME,
	SYSTEM_INFO_MISSING_ERROR_MESSAGE
} from "../../utils/constants";
import { clearAuthState, clearUnauthState, logoutUser } from "../../redux/reducers/auth.reducer";
import { useActuateBarrierMutation } from "../../redux/reducers/dashboard.reducer";
import {
	useGetCamerasListQuery,
	useGetForceSyncStatusQuery,
	useGetPartnerDetailsQuery,
	useGetSiteConfigQuery,
	useGetSocietyConfigQuery
} from "../../redux/reducers/settings.reducer";
import {
	addNotification,
	removeNotification,
	showFeedbackNotification,
	toggleShowNotifications
} from "../../redux/reducers/notifications.reducer";
import { ICameraDetails, INotificationEventData, INotificationListItem } from "../../interfaces";
import { BASE_API_URL, newDashSocket } from "../../utils/apiUtils";
import { RESTRICTED_ACCESS_E, getRestrictedAccessList } from "../../utils/commonUtils";
import { queries } from "../../redux/reducers/queries.reducer";
import EditSocietyDialog from "./EditSocietyDialog";

interface ISidebarItem {
	text: string;
	icon: React.ReactNode;
	path: string;
	children?: Omit<ISidebarItem, "children">[];
}

interface ITimeFilterDetails {
	startTime: string;
	endTime: string;
}

type PageLayoutContextType = {
	timeFilter: ITimeFilterDetails;
	timeFilterType: SelectedRangeType;
	forceSyncStatusLoading: boolean;
	startSyncStatusPolling: () => void;
};

const drawerWidth = 280;

const sidebarItemsList: ISidebarItem[] = [
	{
		text: "Dashboard",
		icon: <DashboardIcon color="inherit" />,
		path: "/dashboard"
	},
	{
		text: "Live View",
		icon: <VideocamIcon color="inherit" />,
		path: "/live-view"
	},
	{
		text: "Data Management",
		icon: <StorageIcon color="inherit" />,
		path: "/data-management",
		children: [
			{
				text: "Authorized List",
				icon: <PlaylistAddCheckIcon color="inherit" />,
				path: "/authorized-list"
			},
			{
				text: "Blacklist",
				icon: <BlockIcon color="inherit" />,
				path: "/blacklist"
			}
		]
	},
	{
		text: "Configuration",
		icon: <SettingsIcon color="inherit" />,
		path: "/configuration",
		children: [
			{
				text: "System Settings",
				icon: <MarginIcon color="inherit" />,
				path: "/system"
			},
			{
				text: "Camera Settings",
				icon: <VideocamIcon color="inherit" />,
				path: "/camera-settings"
			},
			{
				text: "Additional Settings",
				icon: <ConstructionIcon color="inherit" />,
				path: "/additional-settings"
			},
			{
				text: "User Management",
				icon: <SupervisedUserCircleIcon color="inherit" />,
				path: "/user-management"
			},
			{
				text: "Device Status",
				icon: <DevicesIcon color="inherit" />,
				path: "/devices"
			},
			{
				text: "Network Settings",
				icon: <PodcastsIcon color="inherit" />,
				path: "/network-settings"
			}
		]
	}
];

const PageLayout = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const authState = useAppSelector((state) => state.auth);
	const notificationsState = useAppSelector((state) => state.notifications);

	const webSocket = useRef<WebSocket | null>(null);
	const loadingTimer = useRef<NodeJS.Timeout | null>(null);
	const profileMenuAnchorRef = useRef<HTMLDivElement>(null);

	const [mobileOpen, setMobileOpen] = useState<boolean>(false);
	const [expandedSidebarItem, setExpandedSidebarItem] = useState<string>("");
	const [showLoader, setShowLoader] = useState<boolean>(true);
	const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
	const [timeFilter, setTimeFilter] = useState({
		startTime: moment().startOf("day").format(EVENT_LOGS_REQUEST_TIME_FORMAT),
		endTime: moment().endOf("day").format(EVENT_LOGS_REQUEST_TIME_FORMAT)
	});
	const [barrierLoaderMessage, setBarrierLoaderMessage] = useState<string>("");
	const [selectedDateRangeType, setSelectedDateRangeType] = useState<SelectedRangeType>("today");
	const [retryMessage, setRetryMessage] = useState<string>("");
	const [showContactSupportDialog, setShowContactSupportDialog] = useState<boolean>(false);
	const [profileMenuOpen, setProfileMenuOpen] = useState<boolean>(false);
	const [showEditSocietyDialog, setShowEditSocietyDialog] = useState<boolean>(false);
	const [syncStatusPollingEnabled, setSyncStatusPollingEnabled] = useState<boolean>(false);

	// APIS
	const [
		actuateBarrier,
		{
			isLoading: actuateBarrierLoading,
			isSuccess: actuateBarrierSuccess,
			error: actuateBarrierError,
			reset: resetActuateBarrierState
		}
	] = useActuateBarrierMutation();

	const {
		data: siteConfig,
		isLoading: getSiteConfigLoading,
		isFetching: getSiteConfigFetching,
		error: getSiteConfigError,
		refetch: refetchSiteConfig
	} = useGetSiteConfigQuery(null, {
		refetchOnMountOrArgChange: true,
		skip: !authState.user.token || authState.user.token === "invalid"
	});

	// GET FORCE SYNC STATUS
	const {
		data: forceSyncStatus,
		isLoading: getForceSyncStatusLoading,
		isFetching: getForceSyncStatusFetching,
		error: getForceSyncStatusError,
		refetch: refetchGetForceSyncStatus
	} = useGetForceSyncStatusQuery(null, {
		pollingInterval: syncStatusPollingEnabled ? FORCE_SYNC_STATUS_POLLING_INTERVAL * 1000 : 0,
		skip: !siteConfig
	});

	const { data: getSocietyConfigResponse } = useGetSocietyConfigQuery(null, { skip: !siteConfig });
	const societyConfig = getSocietyConfigResponse?.results ?? [];

	const { data: getPartnerDetailsResponse } = useGetPartnerDetailsQuery(null, { skip: !siteConfig });

	const { data: getCamerasListResponse } = useGetCamerasListQuery({}, { skip: !siteConfig });
	const camerasList = useMemo<ICameraDetails[]>(() => {
		if (getCamerasListResponse) return getCamerasListResponse.results;
		return [];
	}, [getCamerasListResponse]);

	const ShowEntryGateControls = useMemo<boolean>(() => {
		if (siteConfig) {
			if (siteConfig.device_type === RPI_DEVICE_NAME) {
				return camerasList.length === 1 && camerasList[0].is_entry;
			}

			return siteConfig.barrier_config !== 1;
		}

		return false;
	}, [camerasList, siteConfig]);

	const ShowExitGateControls = useMemo<boolean>(() => {
		if (siteConfig) {
			if (siteConfig.device_type === RPI_DEVICE_NAME) {
				return camerasList.length === 1 && !camerasList[0].is_entry;
			}

			return siteConfig.barrier_config !== 0;
		}

		return false;
	}, [camerasList, siteConfig]);

	const RestrictedAccess = useMemo<RESTRICTED_ACCESS_E[]>(
		() => getRestrictedAccessList(siteConfig?.cloud_sync_enabled ?? true),
		[siteConfig]
	);

	const sidebarRenderItems = useMemo<ISidebarItem[]>(() => {
		if (siteConfig && siteConfig.mode === CONFIG_MODE_E.MY_GATE) {
			return sidebarItemsList.filter((item) => item.path !== "/data-management");
		}

		return sidebarItemsList;
	}, [siteConfig]);

	function handleOpenContactSupportDialog() {
		setShowContactSupportDialog(true);
	}

	function handleCloseContactSupportDialog() {
		setShowContactSupportDialog(false);
	}

	function handleOpenMobileDrawer() {
		setMobileOpen(true);
	}

	function handleCloseMobileDrawer() {
		setMobileOpen(false);
	}

	function handleExpandSidebarDropdown(path: string) {
		setExpandedSidebarItem(path);
		handleCloseMobileDrawer();
	}

	function handleNavigateToPath(path: string) {
		if (!location.pathname.startsWith(path)) {
			navigate(path);
			handleExpandSidebarDropdown(path);
		}
	}

	function handleNavigateToChildPath(path: string) {
		navigate(path);
		handleCloseMobileDrawer();
	}

	function handleOpenProfileMenu() {
		setProfileMenuOpen(true);
	}

	function handleCloseProfileMenu() {
		setProfileMenuOpen(false);
	}

	function handleOpenEditSocietyDialog() {
		setShowEditSocietyDialog(true);
		handleCloseProfileMenu();
	}

	function handleCloseEditSocietyDialog() {
		setShowEditSocietyDialog(false);
	}

	function handleChangeTimeFilter(startTime: string, endTime: string, filterType: SelectedRangeType) {
		setTimeFilter({
			startTime: startTime,
			endTime: endTime
		});
		setSelectedDateRangeType(filterType);
	}

	function handleLogout() {
		dispatch(logoutUser());
	}

	function clearAllApiStates() {
		dispatch(queries.util.resetApiState());
	}

	function handleLoginAgainClick() {
		dispatch(clearUnauthState());
		clearAllApiStates();
	}

	function handleOpenBarrier(site: "entry" | "exit") {
		setBarrierLoaderMessage("Opening");
		actuateBarrier({ location: site, action: "up" });
	}

	function handleCloseBarrier(site: "entry" | "exit") {
		setBarrierLoaderMessage("Closing");
		actuateBarrier({ location: site, action: "down" });
	}

	const checkAuthNavigate = useCallback(() => {
		if (!authState.user.token || authState.user.token === "invalid") {
			navigate("/login");
			return;
		}

		if (!siteConfig || getSiteConfigLoading || getSiteConfigFetching) {
			return;
		}

		if (loadingTimer.current) {
			clearTimeout(loadingTimer.current);
			loadingTimer.current = null;
			setShowLoadingMessage(false);
		}

		if (typeof siteConfig.mode !== "number" || siteConfig.mode < 0) {
			navigate("/ots");
			return;
		}

		if (!getSocietyConfigResponse) {
			return;
		}

		if (getSocietyConfigResponse.results.length <= 0) {
			navigate("/ots");
			return;
		}

		setShowLoader(false);
	}, [
		authState.user.token,
		getSiteConfigFetching,
		getSiteConfigLoading,
		getSocietyConfigResponse,
		navigate,
		siteConfig
	]);

	function handleToggleShowNotifications() {
		dispatch(toggleShowNotifications());
	}

	function checkMessageIdExists(messageId: string) {
		return !!notificationsState.notifications.find((notificationItem) => notificationItem.messageId === messageId);
	}

	function handleAddNotificationData(data: INotificationListItem) {
		if (checkMessageIdExists(data.messageId)) return;
		dispatch(addNotification(data));
	}

	function handleWebsocketMessageEvent(event: MessageEvent) {
		if (event.data) {
			const eventDetails: INotificationEventData = JSON.parse(event.data);

			if (eventDetails.type === "event") {
				const eventData = eventDetails.data;

				const notificationData: INotificationListItem = {
					messageId: eventData.messageid,
					data: {
						is_auth: eventData.metadata.auth,
						evidence_image: eventData.metadata.frame,
						number_plate_image: eventData.metadata.image,
						is_entry: eventData.metadata.is_entry,
						node_id: eventData.metadata.node,
						node_name: eventData.metadata.node_name,
						number_plate: eventData.metadata.number_plate,
						site_id: eventData.metadata.site,
						site_name: eventData.metadata.site_name,
						timestamp: eventData.metadata.timestamp,
						vehicle_type: eventData.metadata.vtype
					}
				};

				handleAddNotificationData(notificationData);
			}
		}
	}

	function handleRetry() {
		setRetryMessage("");
		setShowLoadingMessage(false);
		refetchSiteConfig();
	}

	function handleStartSyncStatusPolling() {
		refetchGetForceSyncStatus();
	}

	const connectWebsocket = useCallback(() => {
		if (!siteConfig) return;
		if (siteConfig.device_type === RPI_DEVICE_NAME) return;

		const createdSocket = newDashSocket();

		webSocket.current = createdSocket;

		webSocket.current.onopen = (event) => {
			if (event && webSocket.current) webSocket.current.send("LOCALDASH");
		};

		webSocket.current.onmessage = handleWebsocketMessageEvent;

		webSocket.current.onclose = (event) => {
			if (event.code === 1006 && !event.wasClean) connectWebsocket();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteConfig]);

	function checkTabRestriction(path: string): boolean {
		if (path === "/user-management" && RestrictedAccess.includes(RESTRICTED_ACCESS_E.USERS_PAGE)) {
			return true;
		}

		if (path === "/additional-settings" && RestrictedAccess.includes(RESTRICTED_ACCESS_E.ADDITIONAL_SETTINGS_PAGE)) {
			return true;
		}

		return false;
	}

	useEffect(() => {
		connectWebsocket();

		let faviconLink = document.querySelector("link[rel~='icon']");

		if (!faviconLink) {
			faviconLink = document.createElement("link");
			faviconLink.setAttribute("rel", "icon");
			document.getElementsByTagName("head")[0].appendChild(faviconLink);
		}

		faviconLink.setAttribute("href", `${BASE_API_URL}/media/icons/favicon.ico`);

		return () => {
			if (webSocket.current) webSocket.current.close();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectWebsocket]);

	useEffect(() => {
		if (notificationsState.notifications[0] && notificationsState.notifications[0].messageId) {
			setTimeout(() => {
				dispatch(removeNotification(notificationsState.notifications[0].messageId));
			}, NOTIFICATION_AUTO_DISMISS_TIME * 1000);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [notificationsState.notifications[0]?.messageId]);

	useEffect(() => {
		if (!authState.user.token || authState.user.token === "invalid") {
			navigate("/");
			clearAllApiStates();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authState.user.token]);

	useEffect(() => {
		// checkLoginToken();

		const activeScreen = sidebarItemsList.find((item) => location.pathname.startsWith(item.path));
		if (activeScreen && activeScreen.children) {
			setExpandedSidebarItem(activeScreen.path);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		checkAuthNavigate();
	}, [checkAuthNavigate]);

	useEffect(() => {
		if (actuateBarrierSuccess) {
			setBarrierLoaderMessage("");
			resetActuateBarrierState();
		}
	}, [actuateBarrierSuccess, resetActuateBarrierState]);

	useEffect(() => {
		if (getSiteConfigLoading || getSiteConfigFetching) {
			if (!loadingTimer.current) {
				loadingTimer.current = setTimeout(() => {
					setShowLoadingMessage(true);
					loadingTimer.current = null;
				}, 5 * 1000);
			}
		}
	}, [getSiteConfigFetching, getSiteConfigLoading]);

	useEffect(() => {
		if (getSiteConfigError) {
			if ("data" in getSiteConfigError) {
				if (getSiteConfigError.status === 401) {
					dispatch(clearAuthState());
					clearAllApiStates();
				} else if (getSiteConfigError.data && getSiteConfigError.data === DEPLOYMENT_ERROR_MESSAGE) {
					setRetryMessage(DEPLOYMENT_ERROR_MESSAGE);
				} else if (getSiteConfigError.data && getSiteConfigError.data === SYSTEM_INFO_MISSING_ERROR_MESSAGE) {
					setRetryMessage(SYSTEM_INFO_MISSING_ERROR_MESSAGE);
				} else {
					setTimeout(() => {
						refetchSiteConfig();
					}, 5 * 1000);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getSiteConfigError]);

	useEffect(() => {
		if (actuateBarrierError) {
			dispatch(
				showFeedbackNotification({
					message:
						"data" in actuateBarrierError && actuateBarrierError.data
							? String(actuateBarrierError.data)
							: "Failed to operate gate",
					severity: "error"
				})
			);

			resetActuateBarrierState();
		}
	}, [actuateBarrierError, dispatch, resetActuateBarrierState]);

	useEffect(() => {
		if (siteConfig) {
			document.title = siteConfig.product_name ?? "";
		}
	}, [siteConfig]);

	useEffect(() => {
		if (forceSyncStatus) {
			if (forceSyncStatus.status === "pending" || forceSyncStatus.status === "running") {
				setSyncStatusPollingEnabled(true);
			} else {
				if (forceSyncStatus.status === "completed") {
					if (syncStatusPollingEnabled) {
						dispatch(
							showFeedbackNotification({
								message: `Sync Complete!
									Vehicle Owners Status: ${forceSyncStatus.result.vehicle_owners_status ?? "Vehicle owners are up to date!"}
									Vehicles Status: ${forceSyncStatus.result.vehicles_status ?? "Vehicles are up to date!"}`,
								severity: "success"
							})
						);
					}
				}

				setSyncStatusPollingEnabled(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceSyncStatus, syncStatusPollingEnabled]);

	const drawer = (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem" }}>
			<Box className="mobile-drawer-close-icon">
				<IconButton
					sx={{
						color: "#616161",
						padding: { xs: "4px", sm: "6px" }
					}}
					size="small"
					onClick={handleCloseMobileDrawer}
				>
					<CloseIcon color="inherit" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }} />
				</IconButton>
			</Box>

			{process.env.REACT_APP_HIDE_GG_LOGO !== "true" ? (
				<Toolbar classes={{ root: "page-layout-toolbar" }}>
					{/* <GateGuardLogo width="90%" /> */}

					{/* <SvgInline url={`${BASE_API_URL}/media/icons/gate-guard-logo.svg`} /> */}

					<Box sx={{ width: { xs: "80%", sm: "85%", md: "90%" } }}>
						<img src={`${BASE_API_URL}/media/icons/logo.png`} alt={siteConfig?.product_name ?? "Logo"} width="100%" />
					</Box>
				</Toolbar>
			) : null}

			{process.env.REACT_APP_HIDE_GG_LOGO !== "true" ? (
				<Divider sx={{ borderColor: "#C5C1EA33", width: "80%" }} />
			) : null}

			{societyConfig && societyConfig.length > 0 ? (
				<Box
					sx={{
						width: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: "0.5rem",
						marginBottom: "0.75rem",
						marginTop: process.env.REACT_APP_HIDE_GG_LOGO !== "true" ? "0.75rem" : "1rem",
						px: { xs: 1, sm: 1.5 }
					}}
				>
					<LocationOnIcon color="primary" sx={{ fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" } }} />

					<Typography
						variant="body2"
						fontWeight={600}
						color="var(--color-primary-main)"
						overflow="hidden"
						textOverflow="ellipsis"
						sx={{
							fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
							maxWidth: { xs: "120px", sm: "150px", md: "200px" }
						}}
					>
						{societyConfig[0].name}
					</Typography>
				</Box>
			) : null}

			<Divider sx={{ borderColor: "#C5C1EA33", width: "80%" }} />

			<List sx={{ paddingX: { xs: "0.5rem", sm: "0.75rem" }, width: "100%" }}>
				{sidebarRenderItems.map((item, index) => (
					<Fragment key={index}>
						<ListItem
							key={index}
							disablePadding
							classes={{
								root: `sidebar-list-item ${location.pathname.startsWith(item.path) ? "active" : ""}`
							}}
							sx={{ color: location.pathname.startsWith(item.path) ? "white" : "#616161" }}
						>
							<ListItemButton onClick={() => handleNavigateToPath(item.path)}>
								<ListItemIcon sx={{ color: location.pathname.startsWith(item.path) ? "white" : "#616161" }}>
									{item.icon}
								</ListItemIcon>
								<ListItemText
									primary={item.text}
									primaryTypographyProps={{
										fontSize: { xs: "0.875rem", sm: "1rem" }
									}}
								/>

								{item.children ? (
									expandedSidebarItem === item.path ? (
										<ArrowDropUpIcon sx={{ color: "white" }} />
									) : (
										<ArrowDropDownIcon sx={{ color: "#616161" }} />
									)
								) : null}
							</ListItemButton>
						</ListItem>

						{item.children ? (
							<Collapse in={expandedSidebarItem === item.path} timeout="auto" unmountOnExit>
								<List
									component="div"
									disablePadding
									sx={{
										paddingLeft: { xs: "2rem", sm: "2.5rem" },
										paddingY: "0.25rem",
										backgroundColor: "#F8F8FF",
										paddingRight: "0.25rem",
										borderBottomLeftRadius: "7px",
										borderBottomRightRadius: "7px"
									}}
								>
									{item.children.map((childItem) =>
										checkTabRestriction(childItem.path) ? null : (
											<ListItem
												key={childItem.path}
												disablePadding
												classes={{
													root: `sidebar-list-item child ${
														location.pathname.startsWith(`${item.path}${childItem.path}`) ? "active" : ""
													}`
												}}
												sx={{
													color: location.pathname.startsWith(`${item.path}${childItem.path}`) ? "white" : "#616161"
												}}
											>
												<ListItemButton
													sx={{ pl: 2 }}
													// href={`${item.path}${childItem.path}`}
													onClick={() => handleNavigateToChildPath(`${item.path}${childItem.path}`)}
												>
													<ListItemIcon
														sx={{
															color: location.pathname.startsWith(`${item.path}${childItem.path}`)
																? "white"
																: "#616161",
															minWidth: "40px"
														}}
													>
														{childItem.icon}
													</ListItemIcon>
													<ListItemText
														primary={childItem.text}
														primaryTypographyProps={{
															fontSize: { xs: "0.875rem", sm: "1rem" }
														}}
													/>
												</ListItemButton>
											</ListItem>
										)
									)}
								</List>
							</Collapse>
						) : null}
					</Fragment>
				))}
			</List>

			<Box sx={{ marginTop: "auto", width: "100%", paddingTop: "1rem" }}>
				{ShowEntryGateControls || ShowExitGateControls ? (
					<Typography
						variant="h6"
						color="var(--color-primary-main)"
						textAlign="center"
						sx={{ fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" } }}
					>
						Gate Control
					</Typography>
				) : null}

				{siteConfig ? (
					<Box sx={{ display: "flex", paddingX: { xs: "1rem", sm: "1.5rem", md: "2rem" }, gap: "1rem" }}>
						{ShowEntryGateControls ? (
							<Box className="gate-control-actions-wrapper">
								<Typography fontWeight={600} textAlign="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									Entry
								</Typography>

								<Box className="gate-control-button open" onClick={() => handleOpenBarrier("entry")}>
									<UpArrowIconImage width={28} fill="white" />
									<Typography fontWeight={500} color="white" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
										Open
									</Typography>
								</Box>

								{siteConfig.device_type !== RPI_DEVICE_NAME ? (
									<Box className="gate-control-button close" onClick={() => handleCloseBarrier("entry")}>
										<DownArrowIconImage width={28} fill="white" />
										<Typography fontWeight={500} color="white" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
											Close
										</Typography>
									</Box>
								) : null}
							</Box>
						) : null}

						{ShowExitGateControls ? (
							<Box className="gate-control-actions-wrapper">
								<Typography fontWeight={600} textAlign="center" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
									Exit
								</Typography>

								<Box className="gate-control-button open" onClick={() => handleOpenBarrier("exit")}>
									<UpArrowIconImage width={28} fill="white" />
									<Typography fontWeight={500} color="white" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
										Open
									</Typography>
								</Box>

								{siteConfig.device_type !== RPI_DEVICE_NAME ? (
									<Box className="gate-control-button close" onClick={() => handleCloseBarrier("exit")}>
										<DownArrowIconImage width={28} fill="white" />
										<Typography fontWeight={500} color="white" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
											Close
										</Typography>
									</Box>
								) : null}
							</Box>
						) : null}
					</Box>
				) : null}

				{siteConfig && siteConfig.barrier_config >= 0 ? (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem", height: "22px" }}>
						{actuateBarrierLoading && barrierLoaderMessage ? (
							<>
								<GateLoaderIcon width={22} />
								<Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
									{barrierLoaderMessage}
								</Typography>
							</>
						) : null}
					</Box>
				) : null}
			</Box>

			{getPartnerDetailsResponse && getPartnerDetailsResponse.logo_data ? (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						paddingBottom: "1rem",
						gap: "0.5rem",
						px: { xs: 1, sm: 1.5 }
					}}
				>
					<Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
						Solution Partner
					</Typography>

					<img
						src={`data:image/png;base64,${getPartnerDetailsResponse.logo_data}`}
						alt="Solution Partner"
						width="150px"
						style={{
							maxHeight: "70px",
							objectFit: "contain",
							width: "auto",
							maxWidth: "150px"
						}}
					/>

					{getPartnerDetailsResponse.support_email || getPartnerDetailsResponse.support_phone_number ? (
						<Button
							variant="outlined"
							color="primary"
							onClick={handleOpenContactSupportDialog}
							sx={{
								fontSize: { xs: "0.75rem", sm: "0.875rem" },
								padding: { xs: "4px 8px", sm: "6px 12px" }
							}}
						>
							Contact Support
						</Button>
					) : null}
				</Box>
			) : null}
		</Box>
	);

	if (showLoader) {
		if (retryMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<Box sx={{ width: { xs: "280px", sm: "350px", md: "400px" } }}>
						<ServiceOfflineImage width="100%" />
					</Box>

					<Typography
						variant="h5"
						sx={{
							textAlign: "center",
							fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
							px: { xs: 2, sm: 3 }
						}}
					>
						{retryMessage}
					</Typography>

					<Button
						disableElevation
						variant="contained"
						color="primary"
						startIcon={<ReplayIcon />}
						onClick={handleRetry}
						sx={{
							fontSize: { xs: "0.875rem", sm: "1rem" },
							padding: { xs: "8px 16px", sm: "10px 20px" }
						}}
					>
						Retry
					</Button>
				</Box>
			);
		}

		if (showLoadingMessage) {
			return (
				<Box className="service-offline-message-wrapper">
					<Box sx={{ width: { xs: "280px", sm: "350px", md: "400px" } }}>
						<ServiceOfflineImage width="100%" />
					</Box>

					<CircularProgress sx={{ color: "#667eea" }} />

					<Typography
						variant="h5"
						sx={{
							textAlign: "center",
							fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
							px: { xs: 2, sm: 3 }
						}}
					>
						Please wait, the dashboard will be ready once the backend services are up.
					</Typography>
				</Box>
			);
		}

		return (
			<Box sx={{ width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ display: "flex" }}>
				<CssBaseline />

				<Box component="nav" sx={{ width: { xl: drawerWidth }, flexShrink: { xl: 0 } }}>
					<Drawer
						variant="temporary"
						open={mobileOpen}
						onClose={handleCloseMobileDrawer}
						ModalProps={{ keepMounted: true }}
						sx={{
							display: { xs: "block", xl: "none" },
							"& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, border: "none" }
						}}
						PaperProps={{
							sx: { backgroundColor: "#FEFEFE" }
						}}
					>
						{drawer}
					</Drawer>

					<Drawer
						variant="permanent"
						sx={{
							display: { xs: "none", xl: "block" },
							"& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth, border: "none" }
						}}
						PaperProps={{
							sx: { backgroundColor: "#FEFEFE" }
						}}
						open
					>
						{drawer}
					</Drawer>
				</Box>

				<Box
					sx={{
						flexGrow: 1,
						width: { xs: "100%", xl: `calc(100vw - ${drawerWidth}px)` },
						display: "flex",
						flexDirection: "column",
						height: "100vh"
					}}
				>
					<AppBar
						sx={{
							backgroundColor: "#FEFEFE",
							padding: { xs: "0.5rem 0.25rem 0.5rem 0", sm: "0.75rem 0.5rem 0.75rem 0", md: "1rem 0.5rem 1rem 0" }
							// borderBottom: "1px solid #f2f1fb"
						}}
						position="relative"
						elevation={0}
					>
						<Box className="appbar-content-wrapper">
							<Box>
								<IconButton
									sx={{
										color: "#616161",
										display: { xs: "block", xl: "none" },
										padding: { xs: "4px", sm: "6px", md: "8px" }
									}}
									size="small"
									onClick={handleOpenMobileDrawer}
								>
									<MenuIcon color="inherit" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" } }} />
								</IconButton>
							</Box>

							<Box className="header-actions-wrapper">
								{siteConfig && siteConfig.device_type !== RPI_DEVICE_NAME ? (
									<Box className="show-notification-toggle" onClick={handleToggleShowNotifications}>
										{notificationsState.showNotifications ? (
											<NotificationsIcon
												color="primary"
												sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" } }}
											/>
										) : (
											<NotificationsOffIcon
												color="primary"
												sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" } }}
											/>
										)}
									</Box>
								) : null}

								{location.pathname.startsWith("/dashboard") ? (
									<DateRangeSelectionModal
										onDateRangeChange={handleChangeTimeFilter}
										defaultRangeType={selectedDateRangeType}
										defaultValue={{ start: timeFilter.startTime, end: timeFilter.endTime }}
									/>
								) : null}

								<Box
									aria-haspopup
									component="div"
									id="profile-button"
									aria-controls="profile-menu"
									className="header-avatar-wrapper"
									ref={profileMenuAnchorRef}
									aria-expanded={profileMenuOpen}
									onClick={handleOpenProfileMenu}
								>
									<Typography
										variant="body2"
										fontWeight={500}
										sx={{
											fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
											display: { xs: "none", sm: "block" }
										}}
									>
										{authState.user.username ?? ""}
									</Typography>

									<Avatar
										sx={{
											width: { xs: "20px", sm: "22px", md: "24px" },
											height: { xs: "20px", sm: "22px", md: "24px" },
											fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
											bgcolor: "var(--color-primary-main)"
										}}
									>
										{(authState.user.username ?? "").slice(0, 1).toUpperCase()}
									</Avatar>
								</Box>

								<Menu
									id="profile-menu"
									open={profileMenuOpen}
									anchorEl={profileMenuAnchorRef.current}
									onClose={handleCloseProfileMenu}
									MenuListProps={{ "aria-labelledby": "profile-button" }}
									disableAutoFocusItem
									sx={{
										"& .MuiPaper-root": {
											minWidth: { xs: "200px", sm: "220px" }
										}
									}}
								>
									{!RestrictedAccess.includes(RESTRICTED_ACCESS_E.EDIT_BUILDING_CONFIG) ? (
										<MenuItem disableRipple onClick={handleOpenEditSocietyDialog}>
											<ListItemIcon>
												<ApartmentIcon fontSize="small" />
											</ListItemIcon>

											<ListItemText>
												<Typography variant="body2">Edit Society</Typography>
											</ListItemText>
										</MenuItem>
									) : null}

									<MenuItem disableRipple onClick={handleLogout}>
										<ListItemIcon>
											<LogoutIcon fontSize="small" />
										</ListItemIcon>

										<ListItemText>
											<Typography variant="body2">Logout</Typography>
										</ListItemText>
									</MenuItem>
								</Menu>
							</Box>
						</Box>
					</AppBar>

					<Box component="main" className="screen-main-component">
						<Box
							sx={{
								padding: { xs: "0.5rem", sm: "0.75rem", md: "1rem", xl: "1rem" },
								display: "flex",
								flexDirection: "column",
								minHeight: "100%"
							}}
							className="screen-outlet-wrapper"
						>
							<Outlet
								context={{
									timeFilter: timeFilter,
									timeFilterType: selectedDateRangeType,
									startSyncStatusPolling: handleStartSyncStatusPolling,
									forceSyncStatusLoading:
										getForceSyncStatusLoading ||
										getForceSyncStatusFetching ||
										(!forceSyncStatus && !getForceSyncStatusError) ||
										forceSyncStatus?.status === "pending" ||
										forceSyncStatus?.status === "running"
								}}
							/>
						</Box>
					</Box>
				</Box>

				<Dialog open={authState.unauthorizedError} maxWidth="xs" fullWidth>
					<DialogContent>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
							<Typography variant="h6" textAlign={"center"}>
								Your login session has expired
							</Typography>

							<Button variant="contained" sx={{ marginTop: 2 }} onClick={handleLoginAgainClick}>
								Login Again
							</Button>
						</Box>
					</DialogContent>
				</Dialog>
			</Box>

			<Dialog open={showContactSupportDialog} onClose={handleCloseContactSupportDialog} maxWidth="sm" fullWidth>
				<DialogTitle textAlign="center" classes={{ root: "dialog-title-root primary" }}>
					Support
					<Box className="close-dialog-icon-wrapper">
						<IconButton color="inherit" onClick={handleCloseContactSupportDialog}>
							<CloseIcon color="inherit" />
						</IconButton>
					</Box>
				</DialogTitle>

				<DialogContent classes={{ root: "dialog-content-root" }}>
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem" }}>
						<Typography variant="h6" fontWeight={400}>
							{getPartnerDetailsResponse?.name ?? ""}
						</Typography>

						{getPartnerDetailsResponse ? (
							<img
								src={`data:image/png;base64,${getPartnerDetailsResponse.logo_data}`}
								alt="Solution Partner"
								width="200px"
							/>
						) : null}
					</Box>

					<Box
						sx={{
							display: "flex",
							width: "100%",
							alignItems: "center",
							justifyContent: "space-evenly",
							marginTop: "1rem"
						}}
					>
						{getPartnerDetailsResponse && getPartnerDetailsResponse.support_email ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "0.5rem",
									color: "var(--color-primary-main)"
								}}
							>
								<EmailIcon />

								<Link href={`mailto:${getPartnerDetailsResponse.support_email}`}>
									{getPartnerDetailsResponse.support_email}
								</Link>
							</Box>
						) : null}

						{getPartnerDetailsResponse && getPartnerDetailsResponse.support_phone_number ? (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "0.5rem",
									color: "var(--color-primary-main)"
								}}
							>
								<LocalPhoneIcon />

								<Link href={`tel:${getPartnerDetailsResponse.support_phone_number}`}>
									{getPartnerDetailsResponse.support_phone_number}
								</Link>
							</Box>
						) : null}
					</Box>
				</DialogContent>
			</Dialog>

			<EditSocietyDialog open={showEditSocietyDialog} onClose={handleCloseEditSocietyDialog} />
		</>
	);
};

export default PageLayout;

export function usePageLayoutContext() {
	return useOutletContext<PageLayoutContextType>();
}
